import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type Product = Tables<"products">;
export type Variant = Tables<"product_variants">;
export type ProductOption = Tables<"product_options">;

export interface ProductDetail {
  product: Product;
  options: ProductOption[];
  variants: Variant[];
  stockByVariant: Record<string, { available: number; reserved: number; status: string }>;
  images: { id: string; storage_key: string; alt_text: string | null; is_primary: boolean }[];
  /**
   * Si el producto figura en algun pedido. Decide si se puede borrar o solo
   * archivar: order_items lo referencia con ON DELETE RESTRICT, asi que la
   * base rechazaria el borrado de todas formas. Saberlo antes permite ofrecer
   * la accion correcta en lugar de dejar que falle al pulsarla.
   */
  vendido: boolean;
}

export async function fetchProductDetail(id: string): Promise<ProductDetail> {
  const { data: product, error } = await supabase
    .from("products")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;

  const [options, variants, images, stock, vendido] = await Promise.all([
    supabase.from("product_options").select("*").eq("product_id", id).order("position"),
    supabase.from("product_variants").select("*").eq("product_id", id).order("position"),
    supabase
      .from("product_images")
      .select("id, storage_key, alt_text, is_primary")
      .eq("product_id", id)
      .is("deleted_at", null)
      .order("is_primary", { ascending: false })
      .order("sort_order"),
    supabase
      .from("inventory_status")
      .select("variant_id, quantity_available, quantity_reserved, stock_status")
      .eq("product_id", id),
    // head:true no trae filas, solo la cuenta; y con limit(1) basta saber si
    // hay alguna. Un producto muy vendido no tiene por que costar mas.
    supabase
      .from("order_items")
      .select("id", { count: "exact", head: true })
      .eq("product_id", id)
      .limit(1),
  ]);

  const stockByVariant: ProductDetail["stockByVariant"] = {};
  for (const s of stock.data ?? []) {
    if (!s.variant_id) continue;
    stockByVariant[s.variant_id] = {
      available: s.quantity_available ?? 0,
      reserved: s.quantity_reserved ?? 0,
      status: s.stock_status ?? "ok",
    };
  }

  return {
    product,
    options: options.data ?? [],
    variants: variants.data ?? [],
    images: images.data ?? [],
    stockByVariant,
    // Ante la duda se asume vendido: ofrecer archivar de mas es inocuo,
    // ofrecer borrar de mas termina en un error al pulsar.
    vendido: vendido.error ? true : (vendido.count ?? 0) > 0,
  };
}

export interface ProductDraft {
  name: string;
  slug: string;
  /**
   * 'physical' lleva inventario y reserva stock al vender; 'service' no.
   * La restriccion products_kind_check (0038) solo admite esos dos valores.
   */
  kind: string;
  short_description: string | null;
  description: string | null;
  category_id: string | null;
  brand_id: string | null;
  active: boolean;
  featured: boolean;
  seo_title: string | null;
  seo_description: string | null;
}

export async function updateProduct(id: string, draft: ProductDraft) {
  const { error } = await supabase.from("products").update(draft).eq("id", id);
  if (error) throw error;
}

export interface NewProduct {
  organizationId: string;
  name: string;
  slug: string;
  kind: string;
  categoryId: string | null;
  sku: string;
  price: string;
}

/**
 * Alta de producto con su primera variante.
 *
 * Nace DESPUBLICADO aunque la columna venga con `active` en true: un producto
 * recien creado no tiene fotos, ni ficha, ni stock, y publicarlo de inmediato
 * lo pondria asi en la tienda. Se publica desde el detalle cuando esta listo.
 *
 * Toda venta pasa por una variante —`create_order` cobra `sale_price` de la
 * variante, no del producto—, asi que un producto sin ninguna no se puede
 * vender. Por eso se crea la primera aqui y no se deja para despues.
 *
 * Los dos inserts no comparten transaccion: PostgREST no la ofrece desde el
 * navegador. Si el segundo falla se borra el primero, porque un producto sin
 * variantes aparece en el catalogo y no se puede vender — es peor que no
 * haberlo creado. El borrado es seguro: nada pudo referenciarlo todavia.
 */
export async function createProduct(input: NewProduct): Promise<string> {
  const { data, error } = await supabase
    .from("products")
    .insert({
      organization_id: input.organizationId,
      name: input.name,
      slug: input.slug,
      kind: input.kind,
      category_id: input.categoryId,
      active: false,
    })
    .select("id")
    .single();

  if (error) throw error;

  const { error: variantError } = await supabase.from("product_variants").insert({
    organization_id: input.organizationId,
    product_id: data.id,
    name: "Única",
    sku: input.sku,
    sale_price: input.price,
  });

  if (variantError) {
    await supabase.from("products").delete().eq("id", data.id);
    throw variantError;
  }

  return data.id;
}

export async function updateVariant(
  id: string,
  patch: {
    sku?: string;
    sale_price?: string;
    compare_at_price?: string | null;
    low_stock_threshold?: number | null;
    active?: boolean;
  },
) {
  const { error } = await supabase.from("product_variants").update(patch).eq("id", id);
  if (error) throw error;
}

/**
 * Archivar: lo saca del catalogo y de la tienda, pero la fila sigue ahi.
 *
 * Es la unica salida para un producto que ya se vendio. `order_items` lo
 * referencia con ON DELETE RESTRICT y guarda nombre y SKU en snapshot: un
 * pedido de hace un ano se sigue leyendo entero. Borrar la fila dejaria esa
 * venta apuntando al vacio, ademas de romper cualquier cuadre contable.
 */
export async function archiveProduct(id: string) {
  const { error } = await supabase
    .from("products")
    .update({ archived_at: new Date().toISOString(), active: false })
    .eq("id", id);
  if (error) throw error;
}

/** Devuelve un producto archivado al catalogo. */
export async function restoreProduct(id: string) {
  const { error } = await supabase
    .from("products")
    .update({ archived_at: null })
    .eq("id", id);
  if (error) throw error;
}

/**
 * Borrado real, solo para lo que nunca se vendio.
 *
 * Todo lo que cuelga del producto —variantes, imagenes, opciones, ficha,
 * costos, niveles de stock— se va en cascada. Lo que NO cascadea es
 * `order_items`: esa clave es RESTRICT, asi que si el producto figura en algun
 * pedido la base rechaza el borrado. Esa negativa es la garantia de verdad;
 * la comprobacion previa de `vendido` solo sirve para no ofrecer un boton que
 * va a fallar.
 *
 * Las imagenes del bucket no se borran aqui: eso lo hace la Edge Function
 * storage-delete, y dejarlas huerfanas es preferible a borrar por error un
 * objeto que otra fila siga usando.
 */
export async function deleteProduct(id: string) {
  const { error } = await supabase.from("products").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------------ ficha tecnica */

export type SpecField = Tables<"product_spec_fields">;
export type SpecValue = Tables<"product_specs">;

/**
 * Campos aplicables a un producto: los generales de la organizacion mas los
 * de su categoria. «Voltaje» no debe aparecer al editar un saco de comida.
 */
export async function fetchSpecFields(
  organizationId: string,
  categoryId: string | null,
): Promise<SpecField[]> {
  const { data, error } = await supabase
    .from("product_spec_fields")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("active", true)
    .order("position");

  if (error) throw error;

  return (data ?? []).filter((f) => !f.category_id || f.category_id === categoryId);
}

export async function fetchSpecValues(productId: string): Promise<SpecValue[]> {
  const { data, error } = await supabase
    .from("product_specs")
    .select("*")
    .eq("product_id", productId);

  if (error) throw error;
  return data ?? [];
}

/**
 * Guarda un valor. La validacion de tipo, rango y opciones la hace el trigger
 * de PostgreSQL: aqui solo se decide en que columna va segun el tipo del campo.
 */
export async function saveSpec(input: {
  organizationId: string;
  productId: string;
  variantId: string | null;
  field: SpecField;
  value: string | boolean | string[] | null;
}) {
  const { organizationId, productId, variantId, field, value } = input;

  const vacio =
    value === null ||
    value === "" ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

  if (vacio) {
    let del = supabase
      .from("product_specs")
      .delete()
      .eq("product_id", productId)
      .eq("field_id", field.id);
    del = variantId ? del.eq("variant_id", variantId) : del.is("variant_id", null);
    const { error } = await del;
    if (error) throw error;
    return;
  }

  const textoPlano = ["text", "select", "color"].includes(field.data_type);

  const row = {
    organization_id: organizationId,
    product_id: productId,
    variant_id: variantId,
    field_id: field.id,
    value_text: textoPlano ? String(value) : null,
    // numeric viaja como string en los dos sentidos: no se convierte a number
    // en ningun punto del camino.
    value_number:
      field.data_type === "number" ? String(value).replace(",", ".").trim() : null,
    value_bool: field.data_type === "boolean" ? Boolean(value) : null,
    value_text_array:
      field.data_type === "multiselect" && Array.isArray(value) ? value : null,
    value_date: field.data_type === "date" ? String(value) : null,
  };

  // Sin onConflict con el indice parcial: se borra y se inserta, que ademas
  // deja el trigger de validacion como unica puerta.
  let del = supabase
    .from("product_specs")
    .delete()
    .eq("product_id", productId)
    .eq("field_id", field.id);
  del = variantId ? del.eq("variant_id", variantId) : del.is("variant_id", null);
  await del;

  const { error } = await supabase.from("product_specs").insert(row);
  if (error) throw error;
}

export async function fetchSpecGaps(productId: string) {
  const { data, error } = await supabase.rpc("product_spec_gaps", {
    p_product_id: productId,
  });
  if (error) throw error;
  return data;
}

export async function fetchTaxonomy(organizationId: string) {
  const [categories, brands] = await Promise.all([
    supabase
      .from("categories")
      .select("id, name, parent_id")
      .eq("organization_id", organizationId)
      .eq("active", true)
      .order("sort_order"),
    supabase
      .from("brands")
      .select("id, name")
      .eq("organization_id", organizationId)
      .order("name"),
  ]);

  if (categories.error) throw categories.error;
  if (brands.error) throw brands.error;

  // Se muestran como «Padre › Hija» para que el desplegable tenga sentido sin
  // tener que dibujar un arbol.
  const nameById = new Map((categories.data ?? []).map((c) => [c.id, c.name]));
  const labelled = (categories.data ?? []).map((c) => ({
    id: c.id,
    label: c.parent_id ? `${nameById.get(c.parent_id) ?? "?"} › ${c.name}` : c.name,
  }));

  return { categories: labelled, brands: brands.data ?? [] };
}

/* ----------------------------------------------------- generacion de variantes */

export interface GenerateResult {
  created: number;
  reactivated: number;
  kept: number;
  deactivated: number;
  total: number;
}

/**
 * La logica vive en PostgreSQL: decide que combinaciones faltan, cuales
 * reactivar y cuales desactivar, y lo hace en una transaccion. Aqui solo se
 * envia la seleccion.
 */
export async function generateVariants(
  productId: string,
  options: { position: number; field_id: string | null; name: string; values: string[] }[],
  defaultPrice: string | null,
): Promise<GenerateResult> {
  const { data, error } = await supabase.rpc("generate_product_variants", {
    p_product_id: productId,
    p_options: options,
    p_default_price: defaultPrice ?? undefined,
  });

  if (error) throw error;
  return data as unknown as GenerateResult;
}
